import { Info } from "lucide-react";
import { Button } from "../../../ui/common/button";
import { Card, CardContent } from "../../../ui/common/card";
import { Skeleton } from "../../../ui/common/skeleton";
import { MAX_VISIBLE_CHANGES, VERSION_HISTORY_COPY } from "../constants/versionHistory.constants";
import { resolveVisibleChanges } from "../utils/versionHistory.utils";

interface VersionChangeReasonBoxProps {
  versionLabel: string;
  reason: string | null;
  changes: string[];
  isLoading: boolean;
  isError: boolean;
  showAllChanges: boolean;
  onToggleShowAllChanges: () => void;
}

/**
 * Decision 4: reason + mapping changes, in a GREEN left-border box (not the yellow
 * `.drawingChangesBox` this replaces).
 */
export const VersionChangeReasonBox = ({
  versionLabel,
  reason,
  changes,
  isLoading,
  isError,
  showAllChanges,
  onToggleShowAllChanges
}: VersionChangeReasonBoxProps) => {
  const visibleChanges = resolveVisibleChanges(changes, showAllChanges, MAX_VISIBLE_CHANGES);

  return (
    <Card className="tw-w-full tw-border-border tw-border-l-4 tw-border-l-success tw-bg-success/10 tw-py-0 tw-gap-0">
      <CardContent className="tw-flex tw-items-start tw-gap-3 tw-p-4">
        <Info className="tw-h-4 tw-w-4 tw-text-muted-foreground tw-mt-0.5 tw-shrink-0" />
        <div className="tw-text-sm tw-text-foreground tw-space-y-2 tw-w-full">
          <p className="tw-font-medium">{reason || VERSION_HISTORY_COPY.noReasonProvided}</p>

          {isLoading ? (
            <div className="tw-space-y-1.5">
              <Skeleton className="tw-h-3 tw-w-3/4" />
              <Skeleton className="tw-h-3 tw-w-1/2" />
            </div>
          ) : isError ? (
            <p className="tw-text-sm tw-text-muted-foreground">{VERSION_HISTORY_COPY.changesUnavailable}</p>
          ) : (
            <>
              <p className="tw-text-sm tw-text-muted-foreground">
                {changes.length > 0 ? VERSION_HISTORY_COPY.changedValuesTitle : VERSION_HISTORY_COPY.noMappingChanges}
              </p>
              {changes.length > 0 ? (
                <ul className="tw-ml-5 tw-list-disc tw-text-xs tw-text-muted-foreground tw-space-y-0.5">
                  {visibleChanges.map((change, index) => (
                    <li key={index}>{change}</li>
                  ))}
                </ul>
              ) : null}
              {changes.length > MAX_VISIBLE_CHANGES ? (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="tw-h-auto tw-p-0 tw-text-xs"
                  onClick={onToggleShowAllChanges}
                >
                  {showAllChanges ? VERSION_HISTORY_COPY.viewLess : VERSION_HISTORY_COPY.viewMore}
                </Button>
              ) : null}
            </>
          )}

          <p className="tw-sr-only">{versionLabel}</p>
        </div>
      </CardContent>
    </Card>
  );
};
